import { useMemo } from "react";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import {
  useDeleteGoogleBusinessAccountLocation,
  useGetGoogleBusinessAccountLocations,
} from "@/business/google-business-query";

interface DeleteGoogleBusinessLocationModalProps {
  businessId: string;
  locationId: string | null;
  onClose: () => void;
}

export const DeleteGoogleBusinessLocationModal = ({
  businessId,
  locationId,
  onClose,
}: DeleteGoogleBusinessLocationModalProps) => {
  const { data: accountLocations = [] } =
    useGetGoogleBusinessAccountLocations(businessId);

  const { mutate: deleteLocation, isPending: isDeleting } =
    useDeleteGoogleBusinessAccountLocation(businessId);

  const isOpen = !!locationId;

  const location = useMemo(
    () => accountLocations.find((loc) => loc.id === locationId),
    [accountLocations, locationId],
  );

  const handleConfirm = () => {
    if (!locationId) return;

    deleteLocation(locationId, {
      onSuccess: () => {
        toast.success("Location deleted successfully");
        onClose();
      },
      onError: (error) => {
        console.error("Failed to delete location:", error);
        toast.error("Failed to delete location", {
          description:
            error instanceof Error ? error.message : "Please try again",
        });
      },
    });
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Google Business Location</DialogTitle>
        </DialogHeader>
        <Stack className="p-6 pt-0" gap="lg" fullWidth>
          <Typography variant="bodyW1" textColor="secondary">
            Are you sure you want to delete the connection to the following
            location?
          </Typography>
          {location && (
            <Stack gap="sm" fullWidth>
              <Typography variant="subtitleW3">{location.title}</Typography>
              <Typography variant="bodyW2" textColor="tertiary">
                {location.location}
              </Typography>
              <Typography variant="bodyW2" textColor="tertiary">
                Account: {location.account}
              </Typography>
            </Stack>
          )}
        </Stack>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} loading={isDeleting}>
            Delete Location
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
